import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, take, takeUntil } from 'rxjs';
import { Amplify } from "aws-amplify"
import { signIn, signOut, getCurrentUser, fetchAuthSession, AuthUser, AuthSession } from 'aws-amplify/auth'
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { RbacService } from '../../../../shared/service/rbac/rbac.service';
import { MatDialog } from '@angular/material/dialog';
import { ResponseModel } from '../../../../shared/models/base-response.model';
import { ThemeConfig } from '../../../../shared/service/theme.service';
import { TerminalStatus } from '../../../../shared/enums/base.enums';
import { TerminalsService } from '../../../../shared/service/terminals/terminals.service';

export interface IUser {
  email: string;
  password: string;
  showPassword?: boolean;
  code?: string;
  name?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CognitoService {

  private authenticationSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private router: Router,
    private rbacService: RbacService,
    private dialog: MatDialog,
     private terminalService: TerminalsService,
    // private authService:AuthService
  ) {
    Amplify.configure({
      Auth: {
        Cognito: {
          ...environment.cognito,
        },
      },
    })
  }

  public signIn(user: IUser): Promise<any> {
    return signIn({
      username: user.email,
      password: user.password,
      options: {
        authFlowType: 'USER_PASSWORD_AUTH'
      }
    })
      .then(({ nextStep }) => {
        if (nextStep.signInStep == 'DONE') {
          // this.authService.setupUserInteractionListeners()
          // this.router.navigate(['/inicio']);
        }
      })
      .catch((error: any) => {
        console.error("Sign In Error:", error);
        throw error;
      });
  }

  public signOut(closeAll: boolean = false): Promise<any> {
    return signOut()
      .then((value) => {
        if (closeAll) {
          this.dialog.closeAll()
        }

        const localData = localStorage.getItem('themeConfig');
        let routeSegment = '';

        if (localData) {
          try {
            const parsed: ThemeConfig = JSON.parse(localData);
            routeSegment = parsed.nameByRoute || ''; // convención lowercase opcional
          } catch (e) {
            console.error('Error al parsear themeConfig', e);
          }
        }

        // console.log('sesión cerrada')
        this.rbacService.closeLogSession()
          .pipe(take(1), takeUntil(this._destroying$))
          .subscribe({
            next: async () => {
              this.router.navigate(['/login', routeSegment].filter(Boolean));
              this.terminalService.changeStatus(TerminalStatus.INACTIVE).subscribe();
              this.clearLocalStorage();
            },
            error: (err) => {
              console.error('Error al cerrar sesión en RBAC', err);
              this.router.navigate(['/login', routeSegment].filter(Boolean));
              this.clearLocalStorage();
            },
          });
      })
      .catch((error: any) => {
        console.error("Sign Out Error:", error);
        throw error;
      });
  }

  public isAuthenticated(): Promise<boolean> {
    if (this.authenticationSubject.value) {
      return Promise.resolve(true);
    }
    return this.getUser()
      .then(user => !!user)
      .catch(() => false);
  }

  public getUser(): Promise<AuthUser | null> {
    return getCurrentUser()
      .catch((error: any) => {
        return null; // Return null on error
      });
  }

  public getUserSession(): Promise<AuthSession | void> {
    return fetchAuthSession()
      .catch((error: any) => {
        console.error("Get User Session Error:", error);
      }
      )
  }

  private clearLocalStorage() {
    const keyToKeep = 'themeConfig';

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key: string | null = localStorage.key(i);
      if (key !== keyToKeep) {
        localStorage.removeItem(key!);
      }
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
